import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Checkbox,
  Chip,
  Tooltip,
  Snackbar,
  Alert,
  Card,
  CardContent,
  CardActions,
  Button,
  CircularProgress,
  Skeleton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Pagination,
  TableSortLabel,
} from "@mui/material";
import { ResizableTable } from "./ResizableTable";
import {
  ContentCopy,
  Visibility,
  Link as LinkIcon,
  Delete,
  Movie,
  MusicNote,
  Description,
  InsertDriveFile,
} from "@mui/icons-material";
import { BucketId, FileMetadata, PaginationInfo, SortField, SortState } from "../types/File";
import { fileService } from "../services/fileService";
import { getDisplayFileName, getFileCategory, getFileTypeLabel, formatRelativeTime } from "../utils/fileDisplay";
import { FilePreview } from "./FilePreview";

interface FileListProps {
  files: FileMetadata[];
  pagination: PaginationInfo | null;
  viewMode?: "table" | "grid";
  loading?: boolean;
  bucket: BucketId;
  filtered?: boolean;
  sort?: SortState;
  onSort?: (field: SortField) => void;
  onPageChange: (page: number) => void;
  onFileDeleted: () => void;
}

export const FileList: React.FC<FileListProps> = ({
  files,
  pagination,
  viewMode = "table",
  loading = false,
  bucket,
  filtered = false,
  sort,
  onSort,
  onPageChange,
  onFileDeleted,
}) => {
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    file: FileMetadata | null;
    deleting: boolean;
  }>({
    open: false,
    file: null,
    deleting: false,
  });

  const [bulkDelete, setBulkDelete] = useState<{ open: boolean; deleting: boolean }>({
    open: false,
    deleting: false,
  });

  const [previewFile, setPreviewFile] = useState<FileMetadata | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [thumbErrors, setThumbErrors] = useState<Set<string>>(new Set());

  // Reset selection whenever the visible page/data changes.
  useEffect(() => {
    setSelected(new Set());
  }, [files]);

  // Calculate total pages from pagination info
  const totalPages = useMemo(() => {
    if (!pagination) return 1;
    if (typeof pagination.total === "number" && pagination.total > 0 && pagination.limit > 0) {
      return Math.ceil(pagination.total / pagination.limit);
    }
    if (pagination.hasMore) {
      return pagination.page + 1;
    }
    return Math.max(1, pagination.page);
  }, [pagination]);

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  const copyToClipboard = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setSnackbar({ open: true, message: `${type} copied to clipboard!`, severity: "success" });
    } catch (error) {
      setSnackbar({ open: true, message: "Failed to copy to clipboard", severity: "error" });
    }
  };

  const getFileTypeColor = (type: string) => {
    if (type.startsWith("image/")) return "success";
    if (type.startsWith("video/")) return "info";
    if (type.includes("pdf")) return "error";
    if (type.startsWith("text/")) return "warning";
    return "default";
  };

  const isImage = (type: string) => type.startsWith("image/");

  const renderCategoryIcon = (type: string) => {
    const iconSx = { fontSize: 56, color: "rgba(255, 255, 255, 0.35)" };
    switch (getFileCategory(type)) {
      case "video":
        return <Movie sx={iconSx} />;
      case "audio":
        return <MusicNote sx={iconSx} />;
      case "document":
        return <Description sx={iconSx} />;
      default:
        return <InsertDriveFile sx={iconSx} />;
    }
  };

  const handleCloseSnackbar = () => setSnackbar((prev) => ({ ...prev, open: false }));

  const handleDeleteClick = (file: FileMetadata) => {
    setDeleteDialog({ open: true, file, deleting: false });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDialog.file) return;
    setDeleteDialog((prev) => ({ ...prev, deleting: true }));
    try {
      await fileService.deleteFile(deleteDialog.file.key, bucket);
      onFileDeleted();
      setSnackbar({ open: true, message: "File deleted", severity: "success" });
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Failed to delete file: ${error instanceof Error ? error.message : "Unknown error"}`,
        severity: "error",
      });
    } finally {
      setDeleteDialog({ open: false, file: null, deleting: false });
    }
  };

  const handleDeleteCancel = () => setDeleteDialog({ open: false, file: null, deleting: false });

  const handleBulkDeleteConfirm = async () => {
    const keys = Array.from(selected);
    setBulkDelete({ open: true, deleting: true });
    const results = await Promise.allSettled(keys.map((key) => fileService.deleteFile(key, bucket)));
    const failed = results.filter((r) => r.status === "rejected").length;
    setSelected(new Set());
    setBulkDelete({ open: false, deleting: false });
    if (failed > 0) {
      setSnackbar({
        open: true,
        message: `Deleted ${keys.length - failed} of ${keys.length}; ${failed} failed`,
        severity: "error",
      });
    } else {
      setSnackbar({
        open: true,
        message: `Deleted ${keys.length} file${keys.length !== 1 ? "s" : ""}`,
        severity: "success",
      });
    }
    // Always refresh so files that were deleted still disappear even on a partial failure.
    onFileDeleted();
  };

  const toggleSelect = (key: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const allSelected = files.length > 0 && files.every((f) => selected.has(f.key));
  const someSelected = files.some((f) => selected.has(f.key));

  const toggleSelectAll = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) files.forEach((f) => next.delete(f.key));
      else files.forEach((f) => next.add(f.key));
      return next;
    });

  const clearSelection = () => setSelected(new Set());

  if (loading) {
    if (viewMode === "grid") {
      return (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(1, minmax(0, 1fr))",
              sm: "repeat(2, minmax(0, 1fr))",
              md: "repeat(3, minmax(0, 1fr))",
            },
            gap: 2,
          }}
        >
          {Array.from({ length: 6 }).map((_, i) => (
            <Card key={i}>
              <Skeleton variant="rectangular" height={200} />
              <CardContent>
                <Skeleton width="70%" />
                <Skeleton width="40%" />
                <Skeleton width="30%" />
              </CardContent>
            </Card>
          ))}
        </Box>
      );
    }
    return (
      <Box>
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={i} variant="rectangular" height={48} sx={{ mb: 1, borderRadius: 1 }} />
        ))}
      </Box>
    );
  }

  if (files.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h6" color="text.secondary">
          {filtered ? "No files match your search" : "No files uploaded yet"}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {filtered
            ? "Try a different search term or filter."
            : "Start by uploading some files using the upload area above"}
        </Typography>
      </Paper>
    );
  }

  const totalCount = pagination?.total || files.length;

  const toolbar = (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        mb: 2,
        minHeight: 40,
        gap: 2,
        flexWrap: "wrap",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Checkbox
          size="small"
          checked={allSelected}
          indeterminate={someSelected && !allSelected}
          onChange={toggleSelectAll}
          aria-label="Select all files on this page"
        />
        <Typography variant="subtitle1" color="text.secondary">
          {selected.size > 0
            ? `${selected.size} selected`
            : `${totalCount} file${totalCount !== 1 ? "s" : ""}`}
        </Typography>
      </Box>
      {selected.size > 0 && (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button size="small" onClick={clearSelection}>
            Clear
          </Button>
          <Button
            size="small"
            color="error"
            variant="contained"
            startIcon={<Delete />}
            onClick={() => setBulkDelete({ open: true, deleting: false })}
          >
            Delete selected
          </Button>
        </Box>
      )}
    </Box>
  );

  const gridView = (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "repeat(1, 1fr)", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
        gap: 2,
      }}
    >
      {files.map((file) => {
        const name = getDisplayFileName(file.key, file.filename);
        return (
          <Box key={file.key} sx={{ minWidth: 0 }}>
            <Card>
              <Box
                onClick={() => setPreviewFile(file)}
                sx={{
                  position: "relative",
                  height: 200,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "rgba(255, 255, 255, 0.03)",
                  overflow: "hidden",
                  cursor: "pointer",
                }}
              >
                <Checkbox
                  size="small"
                  checked={selected.has(file.key)}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => toggleSelect(file.key)}
                  aria-label={`Select ${name}`}
                  sx={{
                    position: "absolute",
                    top: 4,
                    left: 4,
                    bgcolor: "rgba(0, 0, 0, 0.4)",
                    "&:hover": { bgcolor: "rgba(0, 0, 0, 0.6)" },
                  }}
                />
                {isImage(file.type) && !thumbErrors.has(file.key) ? (
                  <img
                    src={file.url}
                    alt={name}
                    loading="lazy"
                    onError={() =>
                      setThumbErrors((prev) => {
                        const next = new Set(prev);
                        next.add(file.key);
                        return next;
                      })
                    }
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                ) : (
                  renderCategoryIcon(file.type)
                )}
              </Box>
              <CardContent>
                <Typography variant="h6" noWrap title={name}>
                  {name}
                </Typography>
                <Box sx={{ mt: 1, mb: 2 }}>
                  <Chip
                    label={getFileTypeLabel(file.key, file.type, file.filename)}
                    color={getFileTypeColor(file.type)}
                    size="small"
                    sx={{ mr: 1 }}
                  />
                  <Typography variant="body2" color="text.secondary" component="span">
                    {formatFileSize(file.size)}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" title={formatDate(file.lastModified)}>
                  {formatRelativeTime(file.lastModified)}
                </Typography>
              </CardContent>
              <CardActions sx={{ justifyContent: "space-between" }}>
                <Box>
                  <Tooltip title="Preview">
                    <IconButton onClick={() => setPreviewFile(file)} color="info" aria-label="Preview file">
                      <Visibility fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton onClick={() => handleDeleteClick(file)} color="error" aria-label="Delete file">
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Button
                  startIcon={<LinkIcon />}
                  onClick={() => copyToClipboard(file.url, "URL")}
                  size="small"
                  variant="outlined"
                  sx={{ borderRadius: 2 }}
                >
                  Copy Link
                </Button>
              </CardActions>
            </Card>
          </Box>
        );
      })}
    </Box>
  );

  const headerSx = { fontWeight: 600, color: "#ff2d55" } as const;
  const sortLabelSx = {
    color: "#ff2d55",
    "&.Mui-active": { color: "#ff2d55" },
    "&.Mui-active .MuiTableSortLabel-icon": { color: "#ff2d55 !important" },
    "&:hover": { color: "#ff5d7a" },
  } as const;
  // A column header that sorts when clicked (with a direction arrow), falling back to
  // plain text if no sort handler was provided.
  const sortableHeader = (label: string, field: SortField) => (
    <TableCell sortDirection={sort?.field === field ? sort.dir : false} sx={headerSx}>
      {onSort ? (
        <TableSortLabel
          active={sort?.field === field}
          direction={sort?.field === field ? sort.dir : "asc"}
          onClick={() => onSort(field)}
          sx={sortLabelSx}
        >
          {label}
        </TableSortLabel>
      ) : (
        label
      )}
    </TableCell>
  );

  const tableView = (
    <ResizableTable>
      <TableHead>
        <TableRow>
          {sortableHeader("Name", "name")}
          <TableCell sx={headerSx}>Type</TableCell>
          {sortableHeader("Size", "size")}
          {sortableHeader("Last Modified", "date")}
          <TableCell align="center" sx={headerSx}>
            Actions
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {files.map((file) => {
          const name = getDisplayFileName(file.key, file.filename);
          return (
            <TableRow
              key={file.key}
              hover
              selected={selected.has(file.key)}
              sx={{ "&:hover": { backgroundColor: "rgba(255, 45, 85, 0.05)" } }}
            >
              <TableCell>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Checkbox
                    size="small"
                    checked={selected.has(file.key)}
                    onChange={() => toggleSelect(file.key)}
                    aria-label={`Select ${name}`}
                  />
                  <Typography
                    variant="body2"
                    noWrap
                    title={name}
                    sx={{ cursor: "pointer" }}
                    onClick={() => setPreviewFile(file)}
                  >
                    {name}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell>
                <Chip
                  label={getFileTypeLabel(file.key, file.type, file.filename)}
                  color={getFileTypeColor(file.type)}
                  size="small"
                  sx={{ borderRadius: 1 }}
                />
              </TableCell>
              <TableCell>{formatFileSize(file.size)}</TableCell>
              <TableCell>
                <span title={formatDate(file.lastModified)}>{formatRelativeTime(file.lastModified)}</span>
              </TableCell>
              <TableCell align="center">
                <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
                  <Tooltip title="Copy URL">
                    <IconButton
                      onClick={() => copyToClipboard(file.url, "URL")}
                      color="primary"
                      size="small"
                      aria-label="Copy URL"
                    >
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Preview">
                    <IconButton
                      onClick={() => setPreviewFile(file)}
                      color="info"
                      size="small"
                      aria-label="Preview file"
                    >
                      <Visibility fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      onClick={() => handleDeleteClick(file)}
                      color="error"
                      size="small"
                      aria-label="Delete file"
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </ResizableTable>
  );

  return (
    <Box>
      {toolbar}

      {viewMode === "grid" ? gridView : tableView}

      {pagination && (totalPages > 1 || pagination.hasMore) && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <Pagination
            count={totalPages}
            page={pagination.page || 1}
            onChange={(event, page) => onPageChange(page)}
            color="primary"
            showFirstButton
            showLastButton
            sx={{
              "& .MuiPaginationItem-root": {
                color: "#ffffff",
                "&.Mui-selected": {
                  backgroundColor: "#ff2d55",
                  color: "#ffffff",
                  "&:hover": { backgroundColor: "#e0244a" },
                },
                "&:hover": { backgroundColor: "rgba(255, 45, 85, 0.1)" },
              },
            }}
          />
        </Box>
      )}

      {/* Single-file delete confirmation */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        PaperProps={{
          sx: {
            borderRadius: 2,
            backgroundColor: "#1e1e24",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.4)",
            minWidth: 400,
          },
        }}
      >
        <DialogTitle
          id="delete-dialog-title"
          sx={{
            textAlign: "center",
            color: "#ff2d55",
            fontWeight: 600,
            fontSize: "1.5rem",
            pb: 1,
            fontFamily: "'Inter', sans-serif",
          }}
        >
          🗑️ Delete File
        </DialogTitle>
        <DialogContent sx={{ textAlign: "center", py: 2 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ color: "#ffffff", mb: 1, fontFamily: "'Inter', sans-serif" }}>
              {getDisplayFileName(deleteDialog.file?.key || "", deleteDialog.file?.filename)}
            </Typography>
            <Typography variant="body2" sx={{ color: "#b8b8b8", mb: 2, fontFamily: "'Inter', sans-serif" }}>
              {deleteDialog.file && formatFileSize(deleteDialog.file.size)} •{" "}
              {deleteDialog.file && formatDate(deleteDialog.file.lastModified)}
            </Typography>
            <Typography variant="body1" sx={{ color: "#ffffff", fontFamily: "'Inter', sans-serif" }}>
              Are you sure you want to delete this file?
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "#ff2d55", mt: 1, fontWeight: 500, fontFamily: "'Inter', sans-serif" }}
            >
              This action cannot be undone.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 3, px: 3 }}>
          <Button
            onClick={handleDeleteCancel}
            disabled={deleteDialog.deleting}
            variant="outlined"
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              borderColor: "rgba(255, 255, 255, 0.2)",
              color: "#ffffff",
              "&:hover": { borderColor: "#00d4ff", backgroundColor: "rgba(0, 212, 255, 0.1)" },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            disabled={deleteDialog.deleting}
            variant="contained"
            startIcon={deleteDialog.deleting ? <CircularProgress size={16} /> : undefined}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              backgroundColor: "#ff2d55",
              "&:hover": { backgroundColor: "#e0244a" },
              "&:disabled": { backgroundColor: "#666" },
            }}
          >
            {deleteDialog.deleting ? "Deleting..." : "Delete File"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk delete confirmation */}
      <Dialog
        open={bulkDelete.open}
        onClose={() => !bulkDelete.deleting && setBulkDelete({ open: false, deleting: false })}
        PaperProps={{
          sx: {
            borderRadius: 2,
            backgroundColor: "#1e1e24",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            minWidth: 400,
          },
        }}
      >
        <DialogTitle sx={{ textAlign: "center", color: "#ff2d55", fontWeight: 600 }}>
          🗑️ Delete {selected.size} file{selected.size !== 1 ? "s" : ""}
        </DialogTitle>
        <DialogContent sx={{ textAlign: "center", py: 2 }}>
          <Typography variant="body1" sx={{ color: "#ffffff" }}>
            Are you sure you want to delete {selected.size} selected file{selected.size !== 1 ? "s" : ""}?
          </Typography>
          <Typography variant="body2" sx={{ color: "#ff2d55", mt: 1, fontWeight: 500 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 3, px: 3 }}>
          <Button
            onClick={() => setBulkDelete({ open: false, deleting: false })}
            disabled={bulkDelete.deleting}
            variant="outlined"
            sx={{ borderRadius: 2, px: 3, py: 1, borderColor: "rgba(255, 255, 255, 0.2)", color: "#ffffff" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleBulkDeleteConfirm}
            color="error"
            disabled={bulkDelete.deleting}
            variant="contained"
            startIcon={bulkDelete.deleting ? <CircularProgress size={16} /> : undefined}
            sx={{ borderRadius: 2, px: 3, py: 1, backgroundColor: "#ff2d55", "&:hover": { backgroundColor: "#e0244a" } }}
          >
            {bulkDelete.deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <FilePreview
        file={previewFile}
        files={files}
        onClose={() => setPreviewFile(null)}
        onNavigate={(f) => setPreviewFile(f)}
        onCopyUrl={(url) => copyToClipboard(url, "URL")}
        onDelete={(file) => {
          setPreviewFile(null);
          handleDeleteClick(file);
        }}
      />

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
